import {useFrame} from "@react-three/fiber";
import React from "react";
import * as THREE from "three";
import {BlobToString, RunInAction_Set} from "web-vcore";
import {store} from "../../../../Store/index.js";
import {RNG_SFC32} from "../../../../Utils/General/RNG/PRNGs/RNG_SFC32.js";
import {ProgressiveLightMap} from "../../../../Utils/ThreeJS/ProgressiveLightMap.js";
import {CalculateSceneCorners, UpdateCamBoundsToEncompassSceneBounds} from "../../../../Utils/ThreeJS/ShadowCamBoundsCalc.js";
import {DoExtraSceneInit, LightmapState} from "./ExtraSetup.js";
import {ZUpGroupHolder} from "./ZUpGroup.js";
import {LHQInstance, sc, useBakedLHQ} from "./Scene.js";
import {Assert} from "js-vextensions";
import {DrawTextureToRenderTarget} from "../../../@Shared/NavBar/TestingPanel/LightmapSettingsUI.js";
import {LHQFull, TestStr} from "../../../../Store/main.js";

const rng = new RNG_SFC32({seed_int: 0});

export const useFrame_lhqScene = (self: LHQInstance, setInitDone: React.Dispatch<React.SetStateAction<boolean>>)=>{ //vars: {renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera})=>{
	//const {renderer, scene, camera} = vars;

	// add function that gets called just prior/during each render call
	useFrame((state, delta)=>{
		// within this scope: update the variables of "renderer", "scene", and "camera" to the latest values (since useFrame does not re-execute its passed callback on re-renders)
		const renderer = state.gl;
		const {scene, camera} = state;
		const lightmapState_global = store.main.testing.vrLightmap;

		// init logic
		// ==========

		// if no lightmap, then we're not initialized; try to do so now
		if (self.modelLoadsDoneTime && Date.now() - self.modelLoadsDoneTime >= lightmapState_global.modelLoadWaitTime && lightmapState_global.lastBake_status == "waitForModelsLoaded") {
			console.log("Models deemed to be done loading. @modelLoadStates", [...self.modelLoadStates]);
			RunInAction_Set(()=>lightmapState_global.lastBake_status = "prepping");
		}

		if (renderer && scene && camera && self.modelLoadsDoneTime && lightmapState_global.lastBake_status == "prepping" && LHQFull() && !useBakedLHQ) {
			// v-added (here)
			scene.background = new THREE.Color( 0x949494 );
			scene.fog = new THREE.Fog(0x949494, 1000 * sc, 3000 * sc);
			renderer.setPixelRatio( window.devicePixelRatio );
			console.log("PixelRatio:", window.devicePixelRatio);
			//renderer.setSize( window.innerWidth, window.innerHeight );
			renderer.shadowMap.enabled = true;
			//renderer.outputColorSpace = THREE.SRGBColorSpace; // default
			//renderer.outputColorSpace = THREE.LinearSRGBColorSpace;

			//const getTime = ()=>performance.now();
			const getTime = ()=>0; // we want stable/predictable results // todo: find way that use `rng` for this in some way, but with returned-values that "are similar" to time passing
			if (lightmapState_global.lastBake_imageOverride) Assert(lightmapState_global.lastBake_imageOverride.image?.width != null, "lastBake_imageOverride must be a THREE.Texture whose .image.width is set");
			console.log("Res:", lightmapState_global.lastBake_imageOverride?.image?.width);
			self.lightmapState = new LightmapState({lightMapRes: lightmapState_global.lastBake_imageOverride?.image?.width ?? lightmapState_global.resolution});
			self.lightmap = new ProgressiveLightMap(renderer, camera, self.lightmapState.lightMapRes, self.lightmapState.params.lightmapIntensity, rng, getTime);
			console.log("Lightmap created!", self.lightmap);

			DoExtraSceneInit({self, renderer, scene, camera});

			(self.lightmap as ProgressiveLightMap).addObjectsToLightMap(self.lightmapObjects);
			console.log("Added objects to lightmap:", self.lightmapObjects.length);

			// special case; if we're trying to load a pre-baked lightmap from the server, then apply that lightmap image, and complete the init now
			if (lightmapState_global.lastBake_imageOverride) {
				RunInAction_Set(()=>lightmapState_global.lastBake_status = "done"); // next step is to just use the lightmap
				//console.log("Using pre-baked lightmap from server:", lightmapState_global.lastBake_imageOverride);
				DrawTextureToRenderTarget(renderer, self.lightmap!.progressiveLightMap2, lightmapState_global.lastBake_imageOverride);
			}
			// else, proceed with regular next step
			else {
				RunInAction_Set(()=>lightmapState_global.lastBake_status = "rngReset"); // next do the rng-reset (just before actual baking)
			}

			//self.initDone_time = Date.now();
			setInitDone(true);
		}

		// regular frame logic
		// ==========

		const cameras = camera instanceof THREE.PerspectiveCamera ? [camera] : camera instanceof THREE.ArrayCamera ? camera.cameras : [];

		// Accumulate Surface Maps
		const opt = self.lightmapState;
		if (self.lightmap && opt && !g.pauseLightmapUpdates && !renderer.xr.isPresenting) {
			const cameraForLightmap = cameras[0];

			const updateDirLights = ()=>{
				// NOTE: during the bake, the dir-lights are re-parented by ProgressiveLightMap into its internal identity-transform scene,
				// so light.position is in WORLD space (y-up) -- NOT ZUpGroup-local. All coordinates computed below are ZUpGroup-local (z-up,
				// matching the Vec3-authored lightOrigins/lightRadius), and converted to world at the end via ZUpGroupHolder.LocalPosToWorld.
				const localToWorld = (v: THREE.Vector3)=>{
					const world = ZUpGroupHolder.LocalPosToWorld(v);
					v.copy(world);
				};

				// Manually Update the Directional Lights
				for (const light of self.dirLights) {
					// Sometimes they will be sampled from the target direction
					// Sometimes they will be uniformly sampled from the upper hemisphere
					if (rng.GetNextFloat() > opt.params.ambientWeight) {
						const selectedLightOrigin = opt.lightOrigins[Math.floor(rng.GetNextFloat() * opt.lightOrigins.length)];
						const randFromNeg1ToPos1 = ()=>(rng.GetNextFloat() * 2) - 1;
						const lightRadius_3j = opt.params.lightRadius.To3JPos();
						// all in ZUpGroup-local (z-up) space
						light.position.set(selectedLightOrigin!.position.x + (randFromNeg1ToPos1() * lightRadius_3j.x),
						                   selectedLightOrigin!.position.y + (randFromNeg1ToPos1() * lightRadius_3j.y),
						                   selectedLightOrigin!.position.z + (randFromNeg1ToPos1() * lightRadius_3j.z));
					} else {
						// Uniform Hemispherical Surface Distribution for Ambient Occlusion
						// z-up: the |abs|-term ("always above the target") is the vertical component, and thus goes on z (all ZUpGroup-local)
						const lambda = Math.acos(2 * rng.GetNextFloat() - 1) - (3.14159 / 2.0);
						const phi = 2 * 3.14159 * rng.GetNextFloat();
						light.position.set(
							 ((Math.cos(lambda) * Math.cos(phi)) * 300 * sc) + opt.object.position.x,
							 ((Math.cos(lambda) * Math.sin(phi)) * 300 * sc) + opt.object.position.y,
							Math.abs((Math.sin(lambda)) * 300 * sc) + opt.object.position.z + 20 * sc
						);
					}

					// light.position is ZUpGroup-local (z-up); the light object itself lives in a world-space (y-up) scene during the bake, so convert
					localToWorld(light.position);

					const worldCorners = CalculateSceneCorners(scene);
					UpdateCamBoundsToEncompassSceneBounds(light, worldCorners, 5);
				}
			};
			const resetLightmap = ()=>{
				updateDirLights();
				self.lightmap!.resetLightmap(cameraForLightmap);
			};
			const doLightmapUpdate = ()=>{
				updateDirLights();

				//const blendWindow = opt.params.directBlend;
				// v-changed; updates start with higher blend-percents, fading over time to 1/[direct-blend count] -- this causes it to converge to a smooth result with much fewer updates needed!
				const blendWindow = (self.lightmap!.updatesSinceReset + 1).KeepAtMost(opt.params.directBlend);

				self.lightmap!.update(cameraForLightmap, blendWindow, opt.params.blurEdges, opt.params.indirectWeight, opt.params.indirectBlend);
				//g.lightmapUpdatesSinceReset = (g.lightmapUpdatesSinceReset ?? 0) + 1;
			};

			if (lightmapState_global.lastBake_status == "rngReset") {
				rng.InitState(); // re-init rng state, so we can get same lightmap result as pre-checked result
				// DEBUG(lightmap); log the ZUpGroup's world transform, so we can verify local light coords convert to what we expect (enable via window.lightmapDebug = true before triggering the bake)
				if ((globalThis as any).lightmapDebug && self.lightmap) {
					const zup = ZUpGroupHolder.group;
					if (zup) {
						zup.updateWorldMatrix(true, false);
						const p = zup.getWorldPosition(new THREE.Vector3());
						const q = zup.getWorldQuaternion(new THREE.Quaternion());
						console.log(`[LightmapDebug] ZUpGroup: worldPos=(${p.toArray().map(a=>a.toFixed(2))}) worldQuat=(${q.toArray().map(a=>a.toFixed(4))})`);
						self.dirLights.forEach(l=>l.updateWorldMatrix(true, false));
						console.log(`[LightmapDebug] dirLight[0]: localPos=(${self.dirLights[0]?.position.toArray().map(a=>a.toFixed(2))}) worldPos=(${self.dirLights[0]?.getWorldPosition(new THREE.Vector3()).toArray().map(a=>a.toFixed(2))})`);
					} else {
						console.warn("[LightmapDebug] ZUpGroupHolder.group is null at bake start!");
					}
				}
				resetLightmap();
				RunInAction_Set(()=>{
					lightmapState_global.lastBake_status = "baking";
					lightmapState_global.lastBake_renderStepsLeft--;
				});
			}

			if (lightmapState_global.lastBake_status == "baking") {
				// do X lightmap renders/updates
				const lightmapUpdatesForThisFrame = lightmapState_global.lastBake_renderStepsLeft.KeepAtMost(lightmapState_global.maxRenderStepsPerFrame);
				for (let i = 0; i < lightmapUpdatesForThisFrame; i++) {
					doLightmapUpdate();
				}
				RunInAction_Set(()=>lightmapState_global.lastBake_renderStepsLeft -= lightmapUpdatesForThisFrame);

				if (lightmapState_global.lastBake_renderStepsLeft <= 0) {
					RunInAction_Set(()=>lightmapState_global.lastBake_status = "done");
				}
			}
		}
	});
};